import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Activity, AlertTriangle, BarChart3, Briefcase, FileText, Gauge, Lock, LogOut, Search, Settings, ShieldCheck, Users } from 'lucide-react';
import './styles.css';

const pages = [
  ['Dashboard', Gauge, '/api/overview'],
  ['Merchants', Users, '/api/shops'],
  ['Subscriptions', ShieldCheck, '/api/subscriptions'],
  ['SERP Usage', BarChart3, '/api/serp-usage'],
  ['Keyword Health', Search, '/api/shops'],
  ['Tracking Jobs', Activity, '/api/tracking-jobs'],
  ['Provider Errors', AlertTriangle, '/api/provider-errors'],
  ['Reports', FileText, '/api/activity'],
  ['Activity', Briefcase, '/api/activity'],
  ['App Review Signals', ShieldCheck, '/api/review-signals'],
  ['Features', Settings, '/api/overview'],
  ['Settings', Lock, '/api/session']
] as const;

function App() {
  const [session, setSession] = useState<boolean | null>(null);
  const [page, setPage] = useState<(typeof pages)[number][0]>('Dashboard');
  const [data, setData] = useState<any>(null);
  const [login, setLogin] = useState({ username: 'owner', password: '' });
  const api = useMemo(() => async (path: string, init?: RequestInit) => {
    const res = await fetch(path, { ...init, headers: { 'content-type': 'application/json', ...(init?.headers || {}) } });
    const json = (await res.json().catch(() => ({}))) as Record<string, any>;
    if (!res.ok) throw new Error(json.error || `Request failed ${res.status}`);
    return json;
  }, []);

  useEffect(() => {
    void api('/api/session').then((result) => setSession(Boolean(result.ok))).catch(() => setSession(false));
  }, [api]);

  useEffect(() => {
    if (!session) return;
    const selected = pages.find(([name]) => name === page);
    if (!selected) return;
    void api(selected[2]).then(setData);
  }, [api, page, session]);

  async function doLogin(event: React.FormEvent) {
    event.preventDefault();
    await api('/api/login', { method: 'POST', body: JSON.stringify(login) });
    setSession(true);
  }

  async function logout() {
    await api('/api/logout', { method: 'POST' });
    setSession(false);
  }

  if (session === null) return <div className="boot">Checking owner session...</div>;
  if (!session) {
    return (
      <main className="login">
        <form onSubmit={doLogin}>
          <div className="mark">SF</div>
          <h1>SERPFlow Owner Admin</h1>
          <input value={login.username} onChange={(event) => setLogin({ ...login, username: event.target.value })} placeholder="Username" />
          <input type="password" value={login.password} onChange={(event) => setLogin({ ...login, password: event.target.value })} placeholder="Password" />
          <button>Log in</button>
        </form>
      </main>
    );
  }

  return (
    <div className="shell">
      <aside>
        <div className="brand"><span className="mark">SF</span><strong>SERPFlow Admin</strong></div>
        {pages.map(([name, Icon]) => (
          <button key={name} className={page === name ? 'active' : ''} onClick={() => setPage(name)}>
            <Icon size={17} /> {name}
          </button>
        ))}
        <button onClick={logout}><LogOut size={17} /> Logout</button>
      </aside>
      <main>
        <header>
          <h1>{page}</h1>
          <p>Operational metadata only. Secrets, raw provider payloads, and merchant strategy details are hidden.</p>
        </header>
        {page === 'Dashboard' ? <Dashboard data={data?.metrics} /> : <Table data={data?.items || []} />}
      </main>
    </div>
  );
}

function Dashboard({ data }: { data?: Record<string, unknown> }) {
  const entries = Object.entries(data || {});
  return <div className="metrics">{entries.map(([key, value]) => <article key={key}><span>{key}</span><strong>{String(value)}</strong></article>)}</div>;
}

function Table({ data }: { data: Array<Record<string, unknown>> }) {
  if (!data.length) return <div className="empty">No records yet.</div>;
  const columns = Object.keys(data[0]).slice(0, 8);
  return (
    <div className="tableWrap">
      <table>
        <thead><tr>{columns.map((col) => <th key={col}>{col.replace(/_/g, ' ')}</th>)}</tr></thead>
        <tbody>{data.map((row, index) => <tr key={String(row.id || index)}>{columns.map((col) => <td key={col}>{String(row[col] ?? 'N/A')}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
